import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import PodcastPlayer from '@/components/PodcastPlayer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Heart, MessageCircle, UserPlus, UserMinus, BadgeCheck, Shield, Headphones, Play, Clock, Plus, X, Trash2, MoreHorizontal, Pencil, Lock, UserCheck, Image, Film, Grid3X3, ZoomIn, MapPin, Link as LinkIcon, Calendar, Sparkles, Upload, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { UserBadge } from '@/components/SupremiumBadge';
import AwardBadges, { ProfileAwardSection } from '@/components/AwardBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = ({ user: currentUser }) => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isOwnProfile = !userId || userId === currentUser?.user_id;
  const [visibility, setVisibility] = useState(null);
  
  // Media gallery state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all', 'photos', 'videos'
  
  // Podcast player state
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  
  // Add podcast form state
  const [showAddPodcast, setShowAddPodcast] = useState(false);
  const [podcastForm, setPodcastForm] = useState({
    title: '',
    description: '',
    audio_url: '',
    thumbnail_url: '',
    episode_number: '',
    season_number: ''
  });
  const [submittingPodcast, setSubmittingPodcast] = useState(false);
  
  // Post editing states
  const [showPostMenu, setShowPostMenu] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  
  // Edit profile modal state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({
    name: '',
    bio: '',
    avatar: '',
    location: '',
    website: '',
    interests: [],
    cover_photo: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
  const [showCoverMenu, setShowCoverMenu] = useState(false);
  
  // Profile gallery state (dedicated photos/videos)
  const [profileGallery, setProfileGallery] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showGalleryUpload, setShowGalleryUpload] = useState(false);
  
  // Interests for editing
  const INTEREST_OPTIONS = [
    { id: 'music', label: 'Music' },
    { id: 'movies', label: 'Movies & TV' },
    { id: 'gaming', label: 'Gaming' },
    { id: 'books', label: 'Books' },
    { id: 'travel', label: 'Travel' },
    { id: 'food', label: 'Food & Cooking' },
    { id: 'fitness', label: 'Fitness' },
    { id: 'art', label: 'Art & Design' },
    { id: 'tech', label: 'Technology' },
    { id: 'business', label: 'Business' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'comedy', label: 'Comedy' },
    { id: 'news', label: 'News' },
    { id: 'sports', label: 'Sports' },
    { id: 'fashion', label: 'Fashion' },
  ];

  // Helper to get full URL for media
  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Handle relative URLs
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Extract all media from posts
  const getAllMedia = () => {
    const media = [];
    posts.forEach(post => {
      // Check for image_url
      if (post.image_url) {
        media.push({
          type: 'image',
          url: getFullUrl(post.image_url),
          post_id: post.post_id,
          created_at: post.created_at,
          likes_count: post.likes_count,
          content: post.content
        });
      }
      // Check for video_url
      if (post.video_url) {
        media.push({
          type: 'video',
          url: getFullUrl(post.video_url),
          thumbnail: getFullUrl(post.video_thumbnail),
          post_id: post.post_id,
          created_at: post.created_at,
          likes_count: post.likes_count,
          content: post.content
        });
      }
      // Check for gif_url
      if (post.gif_url) {
        media.push({
          type: 'gif',
          url: post.gif_url, // GIFs are usually external URLs
          post_id: post.post_id,
          created_at: post.created_at,
          likes_count: post.likes_count,
          content: post.content
        });
      }
      // Check for media array (multiple images/videos)
      if (post.media && Array.isArray(post.media)) {
        post.media.forEach(m => {
          media.push({
            type: m.type || 'image',
            url: m.url,
            thumbnail: m.thumbnail,
            post_id: post.post_id,
            created_at: post.created_at,
            likes_count: post.likes_count,
            content: post.content
          });
        });
      }
    });
    return media;
  };

  const filteredMedia = () => {
    const allMedia = getAllMedia();
    if (mediaFilter === 'photos') {
      return allMedia.filter(m => m.type === 'image' || m.type === 'gif');
    }
    if (mediaFilter === 'videos') {
      return allMedia.filter(m => m.type === 'video');
    }
    return allMedia;
  };

  // Open edit profile modal with current data
  const openEditProfile = () => {
    setEditProfileForm({
      name: user?.name || '',
      bio: user?.bio || '',
      avatar: user?.avatar || '',
      location: user?.location || '',
      website: user?.website || '',
      interests: user?.interests || [],
      cover_photo: user?.cover_photo || ''
    });
    setAvatarPreview(user?.avatar || null);
    setCoverPhotoPreview(user?.cover_photo || null);
    setShowEditProfile(true);
  };
  
  // Handle cover photo upload
  const handleCoverPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPhotoPreview(reader.result);
        setEditProfileForm({ ...editProfileForm, cover_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
    setShowCoverMenu(false);
  };
  
  // Remove cover photo
  const removeCoverPhoto = () => {
    setCoverPhotoPreview(null);
    setEditProfileForm({ ...editProfileForm, cover_photo: '' });
    setShowCoverMenu(false);
  };
  
  // Toggle interest selection
  const toggleInterest = (interestId) => {
    if (editProfileForm.interests.includes(interestId)) {
      setEditProfileForm({
        ...editProfileForm,
        interests: editProfileForm.interests.filter(id => id !== interestId)
      });
    } else if (editProfileForm.interests.length < 8) {
      setEditProfileForm({
        ...editProfileForm,
        interests: [...editProfileForm.interests, interestId]
      });
    } else {
      toast.error('Maximum 8 interests allowed');
    }
  };
  
  // Fetch profile gallery
  const fetchProfileGallery = async () => {
    try {
      const targetUserId = userId || currentUser?.user_id;
      const response = await axios.get(`${API_URL}/users/gallery/${targetUserId}`, { withCredentials: true });
      setProfileGallery(response.data.gallery || []);
    } catch (error) {
      console.log('Gallery not available');
    }
  };
  
  // Upload to profile gallery
  const uploadToGallery = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axios.post(
        `${API_URL}/users/gallery/upload`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setProfileGallery([...response.data.uploaded, ...profileGallery]);
      setShowGalleryUpload(false);
      toast.success(`${response.data.uploaded.length} file(s) uploaded!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploadingMedia(false);
    }
  };
  
  // Delete from gallery
  const deleteFromGallery = async (mediaId) => {
    if (!window.confirm('Delete this from your gallery?')) return;
    try {
      await axios.delete(`${API_URL}/users/gallery/${mediaId}`, { withCredentials: true });
      setProfileGallery(profileGallery.filter(m => m.id !== mediaId));
      toast.success('Deleted from gallery');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Handle avatar file selection
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
        setEditProfileForm({ ...editProfileForm, avatar: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Save profile changes
  const saveProfileChanges = async () => {
    setSavingProfile(true);
    try {
      // Save main profile data
      await axios.put(
        `${API_URL}/users/profile`,
        {
          name: editProfileForm.name,
          bio: editProfileForm.bio,
          avatar: editProfileForm.avatar,
          location: editProfileForm.location,
          website: editProfileForm.website,
          cover_photo: editProfileForm.cover_photo
        },
        { withCredentials: true }
      );
      
      // Save interests separately
      if (editProfileForm.interests.length > 0) {
        await axios.post(
          `${API_URL}/users/interests`,
          { interests: editProfileForm.interests },
          { withCredentials: true }
        );
      }
      
      // Update local state
      setUser({
        ...user,
        name: editProfileForm.name,
        bio: editProfileForm.bio,
        avatar: editProfileForm.avatar,
        location: editProfileForm.location,
        website: editProfileForm.website,
        interests: editProfileForm.interests,
        cover_photo: editProfileForm.cover_photo
      });
      
      setShowEditProfile(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchProfileGallery();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const targetUserId = userId || currentUser?.user_id;
      const [userResponse, postsResponse, podcastsResponse] = await Promise.all([
        axios.get(`${API_URL}/users/profile/${targetUserId}`, {
          withCredentials: true,
        }),
        axios.get(`${API_URL}/posts/user/${targetUserId}`, {
          withCredentials: true,
        }).catch(() => ({ data: [] })), // May fail for private accounts
        axios.get(`${API_URL}/podcasts/user/${targetUserId}`, {
          withCredentials: true,
        }).catch(() => ({ data: [] })), // May fail for private accounts
      ]);
      // Debug logging for badge visibility
      console.log('Profile API Response:', {
        name: userResponse.data?.name,
        username: userResponse.data?.username,
        is_admin: userResponse.data?.is_admin,
        is_verified: userResponse.data?.is_verified,
        visibility: userResponse.data?.visibility
      });
      setUser(userResponse.data);
      setVisibility(userResponse.data.visibility);
      
      // Only set posts if we can view them
      if (userResponse.data.visibility?.can_view_posts) {
        setPosts(postsResponse.data);
        setPodcasts(podcastsResponse.data);
      } else {
        setPosts([]);
        setPodcasts([]);
      }
    } catch (error) {
      console.error('Profile load error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async () => {
    try {
      if (user.is_following || user.follow_status === 'pending') {
        await axios.delete(`${API_URL}/users/follow/${user.user_id}`, {
          withCredentials: true,
        });
        setUser({
          ...user,
          is_following: false,
          follow_status: 'none',
          followers_count: user.is_following ? user.followers_count - 1 : user.followers_count,
        });
        toast.success(user.follow_status === 'pending' ? 'Follow request cancelled' : 'Unfollowed');
      } else {
        const response = await axios.post(`${API_URL}/users/follow/${user.user_id}`, {}, {
          withCredentials: true,
        });
        
        if (response.data.status === 'pending') {
          // Private account - request sent
          setUser({
            ...user,
            follow_status: 'pending',
          });
          toast.success('Follow request sent!');
        } else {
          // Public account - followed directly
          setUser({
            ...user,
            is_following: true,
            follow_status: 'following',
            followers_count: user.followers_count + 1,
          });
          toast.success('Following!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const playPodcast = (podcast) => {
    setCurrentPodcast(podcast);
    setIsPlayerMinimized(false);
  };

  const submitPodcast = async () => {
    if (!podcastForm.title || !podcastForm.audio_url) {
      toast.error('Please fill in title and audio URL');
      return;
    }
    
    setSubmittingPodcast(true);
    try {
      const response = await axios.post(
        `${API_URL}/podcasts/`,
        {
          title: podcastForm.title,
          description: podcastForm.description || null,
          audio_url: podcastForm.audio_url,
          thumbnail_url: podcastForm.thumbnail_url || null,
          episode_number: podcastForm.episode_number ? parseInt(podcastForm.episode_number) : null,
          season_number: podcastForm.season_number ? parseInt(podcastForm.season_number) : null
        },
        { withCredentials: true }
      );
      
      setPodcasts([{ ...response.data, user: currentUser }, ...podcasts]);
      setPodcastForm({ title: '', description: '', audio_url: '', thumbnail_url: '', episode_number: '', season_number: '' });
      setShowAddPodcast(false);
      toast.success('Podcast added!');
    } catch (error) {
      toast.error('Failed to add podcast');
    } finally {
      setSubmittingPodcast(false);
    }
  };

  const deletePodcast = async (podcastId) => {
    try {
      await axios.delete(`${API_URL}/podcasts/${podcastId}`, { withCredentials: true });
      setPodcasts(podcasts.filter(p => p.podcast_id !== podcastId));
      if (currentPodcast?.podcast_id === podcastId) {
        setCurrentPodcast(null);
      }
      toast.success('Podcast deleted');
    } catch (error) {
      toast.error('Failed to delete podcast');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Post edit/delete functions
  const canEditPost = (post) => {
    return post.user_id === currentUser?.user_id || currentUser?.is_admin;
  };

  const startEditing = (post) => {
    setEditingPostId(post.post_id);
    setEditContent(post.content);
    setShowPostMenu(null);
  };

  const cancelEditing = () => {
    setEditingPostId(null);
    setEditContent('');
  };

  const saveEdit = async (postId) => {
    if (!editContent.trim()) {
      toast.error('Post cannot be empty');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/posts/${postId}`,
        { content: editContent },
        { withCredentials: true }
      );
      setPosts(posts.map(p => p.post_id === postId ? { ...p, content: editContent, updated_at: new Date().toISOString() } : p));
      setEditingPostId(null);
      setEditContent('');
      toast.success('Post updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/posts/${postId}`, {
        withCredentials: true,
      });
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
    setShowPostMenu(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="rounded-3xl overflow-hidden border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
            {/* Cover Photo with Edit Option */}
            <div className="h-48 relative group">
              {user?.cover_photo ? (
                <img 
                  src={user.cover_photo} 
                  alt="Cover" 
                  className="w-full h-full object-cover"
                  data-testid="cover-photo"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-primary via-accent to-secondary" />
              )}
              
              {/* Cover Photo Edit Overlay - Own Profile Only */}
              {isOwnProfile && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="relative">
                    <Button
                      onClick={() => setShowCoverMenu(!showCoverMenu)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-white/90 hover:bg-white text-gray-800 shadow-lg"
                      data-testid="edit-cover-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Edit Cover
                    </Button>
                    
                    {/* Cover Menu Dropdown */}
                    {showCoverMenu && (
                      <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 min-w-[160px] z-10">
                        <label className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-700 dark:text-gray-200 text-sm">
                          <Upload className="w-4 h-4" />
                          Upload Photo
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleCoverPhotoChange}
                            className="sr-only"
                            data-testid="cover-upload-input"
                          />
                        </label>
                        {user?.cover_photo && (
                          <button
                            onClick={removeCoverPhoto}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 text-sm w-full text-left"
                            data-testid="remove-cover-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove Photo
                          </button>
                        )}
                        <button
                          onClick={() => setShowCoverMenu(false)}
                          className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 text-sm w-full text-left"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="px-8 pb-8">
              <div className="flex items-end justify-between -mt-16 mb-4">
                <Avatar className="w-32 h-32 border-4 border-white shadow-xl">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-4xl font-bold">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {isOwnProfile ? (
                  <Button
                    onClick={openEditProfile}
                    variant="outline"
                    className="rounded-full font-bold px-6 shadow-lg hover:scale-105 transition-transform border-2"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <Button
                    onClick={toggleFollow}
                    className={`rounded-full font-bold px-6 shadow-lg hover:scale-105 transition-transform ${
                      user?.is_following || user?.follow_status === 'following'
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : user?.follow_status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-primary hover:bg-primary-700 text-white'
                    }`}
                    data-testid="follow-btn"
                  >
                    {user?.is_following || user?.follow_status === 'following' ? (
                      <>
                        <UserMinus className="w-4 h-4 mr-2" />
                        Unfollow
                      </>
                    ) : user?.follow_status === 'pending' ? (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Requested
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Follow
                      </>
                    )}
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-3xl font-heading font-black text-gray-900 dark:text-white" data-testid="profile-name">
                  {user?.name || 'Unknown User'}
                </h1>
                {visibility?.is_private && (
                  <span className="inline-flex items-center" title="Private Account" data-testid="profile-private-badge">
                    <Lock className="w-6 h-6 text-gray-500 drop-shadow-sm" />
                  </span>
                )}
                {/* Supremium Diamond Badge - takes priority */}
                {user?.subscription_tier === 'supremium' ? (
                  <span className="inline-flex items-center" data-testid="profile-supremium-badge">
                    <UserBadge user={user} size="lg" />
                  </span>
                ) : (
                  <>
                    {user?.is_verified === true && (
                      <span className="inline-flex items-center" title="Verified Account" data-testid="profile-verified-badge">
                        <BadgeCheck className="w-7 h-7 text-blue-500 drop-shadow-sm" />
                      </span>
                    )}
                  </>
                )}
                {user?.is_admin === true && (
                  <span className="inline-flex items-center" title="Admin" data-testid="profile-admin-badge">
                    <Shield className="w-7 h-7 text-red-500 drop-shadow-sm" />
                  </span>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">@{user?.username || 'unknown'}</p>
              
              {/* About Me / Bio Section */}
              <div className="mb-4">
                {user?.bio ? (
                  <p className="text-gray-700 dark:text-gray-300">{user.bio}</p>
                ) : isOwnProfile ? (
                  <button 
                    onClick={openEditProfile}
                    className="text-primary hover:underline text-sm"
                  >
                    + Add a bio to tell people about yourself
                  </button>
                ) : null}
              </div>
              
              {/* Location & Website */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                {user?.location && (
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span>{user.location}</span>
                  </div>
                )}
                {user?.website && (
                  <a 
                    href={user.website.startsWith('http') ? user.website : `https://${user.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <LinkIcon className="w-4 h-4" />
                    <span>{user.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
                {user?.created_at && (
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
              
              {/* Interests Tags */}
              {user?.interests && user.interests.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {user.interests.map((interest) => (
                    <span 
                      key={interest}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {interest.charAt(0).toUpperCase() + interest.slice(1).replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
              {!user?.interests?.length && isOwnProfile && (
                <button 
                  onClick={openEditProfile}
                  className="flex items-center gap-1 text-primary hover:underline text-sm mb-4"
                >
                  <Sparkles className="w-4 h-4" />
                  Add your interests
                </button>
              )}

              <div className="flex gap-6">
                <div>
                  <span className="font-bold text-gray-900 dark:text-white text-lg">{user?.posts_count ?? '—'}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">Posts</span>
                </div>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white text-lg">{user?.followers_count ?? '—'}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">Followers</span>
                </div>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white text-lg">{user?.following_count ?? '—'}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">Following</span>
                </div>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white text-lg">{podcasts.length}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">Podcasts</span>
                </div>
              </div>
              
              {/* Award Badges */}
              {user?.user_id && (
                <div className="mt-4">
                  <AwardBadges userId={user.user_id} size="md" maxBadges={6} />
                </div>
              )}
            </div>
          </Card>
        </motion.div>
        
        {/* Award Section - Full display */}
        {user?.user_id && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <ProfileAwardSection userId={user.user_id} />
          </motion.div>
        )}

        {/* Private Account Notice */}
        {visibility && !visibility.can_view_posts && !visibility.is_own_profile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8"
          >
            <Card className="p-8 rounded-3xl text-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <Lock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">This Account is Private</h3>
              <p className="text-gray-500 mb-4">Follow this account to see their posts and podcasts.</p>
              {visibility.follow_status === 'pending' && (
                <p className="text-yellow-600 dark:text-yellow-400 flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  Follow request pending
                </p>
              )}
            </Card>
          </motion.div>
        )}

        {visibility?.can_view_posts && (
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="grid w-full grid-cols-4 rounded-2xl p-1 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
            <TabsTrigger value="posts" className="rounded-xl font-bold" data-testid="posts-tab">Posts</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-xl font-bold" data-testid="gallery-tab">
              <Camera className="w-4 h-4 mr-1" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="podcasts" className="rounded-xl font-bold" data-testid="podcasts-tab">
              <Headphones className="w-4 h-4 mr-1" />
              Podcasts
            </TabsTrigger>
            <TabsTrigger value="media" className="rounded-xl font-bold" data-testid="media-tab">Media</TabsTrigger>
          </TabsList>
          
          <TabsContent value="posts" className="mt-6">
            {posts.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <p className="text-lg text-gray-600 dark:text-gray-400">No posts yet</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {posts.map((post, idx) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md" data-testid={`profile-post-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                            {post.updated_at && <span className="text-xs ml-1">(edited)</span>}
                          </span>
                        </div>
                        {canEditPost(post) && (
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowPostMenu(showPostMenu === post.post_id ? null : post.post_id)}
                              className="rounded-full text-gray-500 hover:text-gray-700 p-1 h-8 w-8"
                              data-testid={`post-menu-btn-${idx}`}
                            >
                              <MoreHorizontal className="w-5 h-5" />
                            </Button>
                            {showPostMenu === post.post_id && (
                              <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-10 min-w-[140px]">
                                <button
                                  onClick={() => startEditing(post)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                  data-testid={`edit-post-btn-${idx}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => deletePost(post.post_id)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                  data-testid={`delete-post-btn-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {editingPostId === post.post_id ? (
                        <div className="mb-4">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="rounded-xl border-2 resize-none mb-2"
                            rows={3}
                            data-testid={`edit-textarea-${idx}`}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEditing}
                              className="rounded-full"
                              data-testid={`cancel-edit-btn-${idx}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => saveEdit(post.post_id)}
                              className="rounded-full bg-primary hover:bg-primary-700 text-white"
                              data-testid={`save-edit-btn-${idx}`}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-gray-800 dark:text-gray-200 mb-2">{post.content}</p>
                          {/* Video */}
                          {post.video_url && (
                            <div className="relative rounded-xl overflow-hidden bg-black mb-3">
                              <video
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                                className="w-full max-h-72 object-contain"
                                controls
                                data-testid={`profile-post-video-${idx}`}
                              />
                            </div>
                          )}
                          {/* Audio */}
                          {post.audio_url && (
                            <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                              <audio
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                                controls
                                className="w-full h-10"
                                preload="metadata"
                              />
                            </div>
                          )}
                          {/* Images */}
                          {post.images && post.images.length > 0 && (
                            <div className={`grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} mb-3`}>
                              {post.images.map((img, i) => (
                                <img key={i} src={img} alt="" className="rounded-xl w-full object-cover max-h-64" data-testid={`profile-post-img-${idx}-${i}`} />
                              ))}
                            </div>
                          )}
                          {/* Single image_url fallback */}
                          {post.image_url && (!post.images || post.images.length === 0) && (
                            <img src={post.image_url} alt="" className="rounded-xl w-full object-cover max-h-64 mb-3" data-testid={`profile-post-image-${idx}`} />
                          )}
                          {/* GIF */}
                          {post.gif_url && (
                            <img src={post.gif_url} alt="Post GIF" className="max-h-64 rounded-xl mb-4" />
                          )}
                        </>
                      )}
                      
                      <div className="flex items-center gap-6 text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Heart className="w-5 h-5" />
                          <span>{post.likes_count}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle className="w-5 h-5" />
                          <span>{post.comments_count}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Profile Gallery Tab - Dedicated photos/videos */}
          <TabsContent value="gallery" className="mt-6">
            {/* Upload Button (own profile only) */}
            {isOwnProfile && (
              <div className="mb-6">
                {!showGalleryUpload ? (
                  <Button
                    onClick={() => setShowGalleryUpload(true)}
                    className="w-full rounded-2xl bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold h-14 shadow-lg"
                    data-testid="add-gallery-btn"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Add to Gallery
                  </Button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="p-6 rounded-3xl border border-pink-200 dark:border-pink-800 shadow-xl bg-pink-50/70 dark:bg-pink-900/20 backdrop-blur-md">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Upload to Gallery</h3>
                        <button onClick={() => setShowGalleryUpload(false)} className="p-1 text-gray-500 hover:text-gray-700">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="text-center py-8">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                          <Camera className="w-10 h-10 text-pink-500" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Upload photos and videos to showcase on your profile
                        </p>
                        <label className="inline-block">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={uploadToGallery}
                            className="sr-only"
                            data-testid="gallery-upload-input"
                          />
                          <span className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-full cursor-pointer transition-colors">
                            {uploadingMedia ? 'Uploading...' : (
                              <>
                                <Upload className="w-5 h-5" />
                                Choose Files
                              </>
                            )}
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-3">
                          Supports JPG, PNG, GIF, MP4, MOV up to 50MB each
                        </p>
                      </div>
                    </Card>
                  </motion.div>
                )}
              </div>
            )}
            
            {/* Gallery Grid */}
            {profileGallery.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 dark:text-gray-400">No gallery items yet</p>
                {isOwnProfile && (
                  <p className="text-sm text-gray-500 mt-2">
                    Upload photos and videos to showcase on your profile!
                  </p>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-3 gap-2 rounded-2xl overflow-hidden" data-testid="profile-gallery-grid">
                {profileGallery.map((item, idx) => (
                  <motion.div
                    key={item.id || idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="relative aspect-square group cursor-pointer overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-xl"
                    onClick={() => setSelectedMedia({
                      type: item.type,
                      url: item.url,
                      created_at: item.created_at,
                      likes_count: 0
                    })}
                    data-testid={`gallery-item-${idx}`}
                  >
                    {item.type === 'video' ? (
                      <>
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <video src={item.url} className="w-full h-full object-cover" muted />
                        )}
                        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                          <Play className="w-3 h-3 text-white fill-white" />
                        </div>
                      </>
                    ) : (
                      <img src={item.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                    
                    {/* Hover Overlay with Delete (for own profile) */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <ZoomIn className="w-6 h-6 text-white" />
                      {isOwnProfile && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFromGallery(item.id);
                          }}
                          className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                          data-testid={`delete-gallery-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="podcasts" className="mt-6">
            {/* Add Podcast Button (own profile only) */}
            {isOwnProfile && (
              <div className="mb-6">
                {!showAddPodcast ? (
                  <Button
                    onClick={() => setShowAddPodcast(true)}
                    className="w-full rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold h-14 shadow-lg"
                    data-testid="add-podcast-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Podcast Episode
                  </Button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="p-6 rounded-3xl border border-purple-200 dark:border-purple-800 shadow-xl bg-purple-50/70 dark:bg-purple-900/20 backdrop-blur-md">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Add Podcast Episode</h3>
                        <button onClick={() => setShowAddPodcast(false)} className="p-1 text-gray-500 hover:text-gray-700">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Title *</label>
                          <Input
                            value={podcastForm.title}
                            onChange={(e) => setPodcastForm({ ...podcastForm, title: e.target.value })}
                            placeholder="Episode title"
                            className="rounded-xl"
                            data-testid="podcast-title-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Audio URL *</label>
                          <Input
                            value={podcastForm.audio_url}
                            onChange={(e) => setPodcastForm({ ...podcastForm, audio_url: e.target.value })}
                            placeholder="https://example.com/episode.mp3"
                            className="rounded-xl"
                            data-testid="podcast-audio-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Thumbnail URL</label>
                          <Input
                            value={podcastForm.thumbnail_url}
                            onChange={(e) => setPodcastForm({ ...podcastForm, thumbnail_url: e.target.value })}
                            placeholder="https://example.com/thumbnail.jpg"
                            className="rounded-xl"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Description</label>
                          <Textarea
                            value={podcastForm.description}
                            onChange={(e) => setPodcastForm({ ...podcastForm, description: e.target.value })}
                            placeholder="What's this episode about?"
                            className="rounded-xl resize-none"
                            rows={3}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Season #</label>
                            <Input
                              type="number"
                              value={podcastForm.season_number}
                              onChange={(e) => setPodcastForm({ ...podcastForm, season_number: e.target.value })}
                              placeholder="1"
                              className="rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Episode #</label>
                            <Input
                              type="number"
                              value={podcastForm.episode_number}
                              onChange={(e) => setPodcastForm({ ...podcastForm, episode_number: e.target.value })}
                              placeholder="1"
                              className="rounded-xl"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={submitPodcast}
                          disabled={submittingPodcast}
                          className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold h-12"
                          data-testid="submit-podcast-btn"
                        >
                          {submittingPodcast ? 'Adding...' : 'Add Podcast'}
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                )}
              </div>
            )}
            
            {/* Podcasts List */}
            {podcasts.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <Headphones className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 dark:text-gray-400">No podcasts yet</p>
                {isOwnProfile && (
                  <p className="text-sm text-gray-500 mt-2">Add your first podcast episode above!</p>
                )}
              </Card>
            ) : (
              <div className="space-y-4">
                {podcasts.map((podcast, idx) => (
                  <motion.div
                    key={podcast.podcast_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card 
                      className={`p-4 rounded-2xl border shadow-lg backdrop-blur-md cursor-pointer transition-all hover:shadow-xl ${
                        currentPodcast?.podcast_id === podcast.podcast_id
                          ? 'border-purple-500 bg-purple-50/70 dark:bg-purple-900/30'
                          : 'border-white/20 bg-white/70 dark:bg-gray-800/70 hover:border-purple-300'
                      }`}
                      onClick={() => playPodcast(podcast)}
                      data-testid={`podcast-item-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Thumbnail */}
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {podcast.thumbnail_url ? (
                            <img src={podcast.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Headphones className="w-8 h-8 text-white" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate" data-testid={`podcast-title-${idx}`}>
                            {podcast.title}
                          </h3>
                          {podcast.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{podcast.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {podcast.season_number && podcast.episode_number && (
                              <span>S{podcast.season_number} E{podcast.episode_number}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(podcast.duration)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Headphones className="w-3 h-3" />
                              {podcast.plays_count || 0} plays
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {podcast.likes_count || 0}
                            </span>
                          </div>
                        </div>
                        
                        {/* Play button */}
                        <div className="flex items-center gap-2">
                          {isOwnProfile && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePodcast(podcast.podcast_id);
                              }}
                              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                              data-testid={`delete-podcast-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <button 
                            className={`p-3 rounded-full transition-all ${
                              currentPodcast?.podcast_id === podcast.podcast_id
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900'
                            }`}
                            data-testid={`play-podcast-${idx}`}
                          >
                            <Play className="w-5 h-5 ml-0.5" />
                          </button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="media" className="mt-6">
            {/* Media Filter Tabs */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setMediaFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  mediaFilter === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                data-testid="media-filter-all"
              >
                <Grid3X3 className="w-4 h-4 inline mr-1" />
                All
              </button>
              <button
                onClick={() => setMediaFilter('photos')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  mediaFilter === 'photos'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                data-testid="media-filter-photos"
              >
                <Image className="w-4 h-4 inline mr-1" />
                Photos
              </button>
              <button
                onClick={() => setMediaFilter('videos')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  mediaFilter === 'videos'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                data-testid="media-filter-videos"
              >
                <Film className="w-4 h-4 inline mr-1" />
                Videos
              </button>
            </div>

            {/* Media Grid */}
            {filteredMedia().length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {mediaFilter === 'all' ? 'No media yet' : `No ${mediaFilter} yet`}
                </p>
                {isOwnProfile && (
                  <p className="text-sm text-gray-500 mt-2">
                    Share photos and videos in your posts to see them here!
                  </p>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-3 gap-1 sm:gap-2 rounded-2xl overflow-hidden" data-testid="media-gallery">
                {filteredMedia().map((media, idx) => (
                  <motion.div
                    key={`${media.post_id}-${idx}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="relative aspect-square group cursor-pointer overflow-hidden bg-gray-100 dark:bg-gray-800"
                    onClick={() => setSelectedMedia(media)}
                    data-testid={`media-item-${idx}`}
                  >
                    {media.type === 'video' ? (
                      <>
                        {media.thumbnail ? (
                          <img
                            src={media.thumbnail}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <video
                            src={media.url}
                            className="w-full h-full object-cover"
                            muted
                          />
                        )}
                        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                          <Play className="w-3 h-3 text-white fill-white" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={media.url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1 text-white">
                        <Heart className="w-5 h-5 fill-white" />
                        <span className="font-semibold">{media.likes_count || 0}</span>
                      </div>
                      <ZoomIn className="w-5 h-5 text-white" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Media Lightbox Modal */}
            <AnimatePresence>
              {selectedMedia && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                  onClick={() => setSelectedMedia(null)}
                  data-testid="media-lightbox"
                >
                  <button
                    onClick={() => setSelectedMedia(null)}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                  
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.9 }}
                    className="max-w-4xl max-h-[90vh] w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {selectedMedia.type === 'video' ? (
                      <video
                        src={selectedMedia.url}
                        controls
                        autoPlay
                        className="w-full max-h-[80vh] rounded-xl"
                      />
                    ) : (
                      <img
                        src={selectedMedia.url}
                        alt=""
                        className="w-full max-h-[80vh] object-contain rounded-xl"
                      />
                    )}
                    
                    {/* Media Info */}
                    <div className="mt-4 text-white">
                      {selectedMedia.content && (
                        <p className="text-sm text-gray-300 mb-2 line-clamp-2">{selectedMedia.content}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {selectedMedia.likes_count || 0} likes
                        </span>
                        <span>
                          {formatDistanceToNow(new Date(selectedMedia.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </TabsContent>
        </Tabs>
        )}
      </div>

      {/* Podcast Player */}
      <AnimatePresence>
        {currentPodcast && (
          <PodcastPlayer
            podcast={currentPodcast}
            onClose={() => setCurrentPodcast(null)}
            isMinimized={isPlayerMinimized}
            onToggleMinimize={() => setIsPlayerMinimized(!isPlayerMinimized)}
          />
        )}
      </AnimatePresence>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowEditProfile(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
              data-testid="edit-profile-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Edit Profile</h2>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-5">
                {/* Avatar Upload */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-white shadow-xl">
                      <AvatarImage src={avatarPreview} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-3xl font-bold">
                        {editProfileForm.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary-700 transition-colors">
                      <Pencil className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="sr-only"
                        data-testid="edit-avatar-input"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Tap to change photo</p>
                </div>

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Display Name
                  </label>
                  <Input
                    value={editProfileForm.name}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, name: e.target.value })}
                    placeholder="Your name"
                    className="rounded-xl border-2 h-12"
                    data-testid="edit-name-input"
                  />
                </div>

                {/* Bio Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    About Me
                  </label>
                  <Textarea
                    value={editProfileForm.bio}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, bio: e.target.value })}
                    placeholder="Tell people about yourself..."
                    className="rounded-xl border-2 resize-none"
                    rows={3}
                    maxLength={300}
                    data-testid="edit-bio-input"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    {editProfileForm.bio?.length || 0}/300
                  </p>
                </div>
                
                {/* Location Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={editProfileForm.location}
                      onChange={(e) => setEditProfileForm({ ...editProfileForm, location: e.target.value })}
                      placeholder="New York, USA"
                      className="rounded-xl border-2 h-12 pl-10"
                      data-testid="edit-location-input"
                    />
                  </div>
                </div>
                
                {/* Website Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Website
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={editProfileForm.website}
                      onChange={(e) => setEditProfileForm({ ...editProfileForm, website: e.target.value })}
                      placeholder="yoursite.com"
                      className="rounded-xl border-2 h-12 pl-10"
                      data-testid="edit-website-input"
                    />
                  </div>
                </div>
                
                {/* Interests Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Interests <span className="text-gray-400 font-normal">({editProfileForm.interests?.length || 0}/8)</span>
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    {INTEREST_OPTIONS.map((interest) => {
                      const isSelected = editProfileForm.interests?.includes(interest.id);
                      return (
                        <button
                          key={interest.id}
                          type="button"
                          onClick={() => toggleInterest(interest.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-primary text-white'
                              : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500 border border-gray-200 dark:border-gray-500'
                          }`}
                          data-testid={`interest-select-${interest.id}`}
                        >
                          {interest.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditProfile(false)}
                    className="flex-1 rounded-full h-12 font-bold"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveProfileChanges}
                    disabled={savingProfile}
                    className="flex-1 rounded-full h-12 bg-primary hover:bg-primary-700 text-white font-bold"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={currentUser} />
    </div>
  );
};

export default Profile;
